import { FC, useEffect } from "react";
import Image from "next/image";

import styles from "./QuickSendAccountingPopup.module.css";

import { Receipt, ExtendedViewItem } from "@/types/receipt";
import {
  ACCOUNTING_WIDGET_TYPE,
  PopupState,
} from "@/constants/popup";
import { translate } from "@/utils/formatters";
import { getStoreName, getButtonText } from "@/utils/extendedViewHelpers";
import { useActiveAccountingWidget } from "@/hooks/useActiveAccountingWidget";
import { ChevronDown } from "@/components/icons";
import { EmailExportSection } from "./EmailExportSection";

interface QuickSendAccountingPopupProps {
  extendedViewItems: ExtendedViewItem[];
  extendedViewImages: { [key: string]: string };
  lang: string;
  receipt: Receipt;
  userId: string;
  receiptId: string;
  showPopup: (popup: PopupState) => void;
  closeButton?: React.ReactNode;
}

export const QuickSendAccountingPopup: FC<QuickSendAccountingPopupProps> = ({
  extendedViewItems,
  extendedViewImages,
  lang,
  receipt,
  userId,
  receiptId,
  showPopup,
  closeButton,
}) => {
  const { activeWidgetId, getStoredEmail, onAccountingSendSuccess } =
    useActiveAccountingWidget();

  const itemIndex = extendedViewItems.findIndex(
    (i) =>
      i.receipt_view_service_settings_id === activeWidgetId &&
      i.extended_view_position === ACCOUNTING_WIDGET_TYPE
  );
  const item = itemIndex >= 0 ? extendedViewItems[itemIndex] : undefined;

  useEffect(() => {
    if (!item) {
      showPopup({ type: ACCOUNTING_WIDGET_TYPE, index: -1 });
    }
  }, [item, showPopup]);

  if (!item) return null;

  const storeName = getStoreName(receipt);
  const imageKey = `${item.extended_view_position}_${itemIndex}`;
  const imageSrc = extendedViewImages[imageKey];

  const accountingOptions = extendedViewItems.filter(
    (i) => i.extended_view_position === ACCOUNTING_WIDGET_TYPE
  );
  const canSwitch = accountingOptions.length > 1;

  const openSwitcher = () =>
    showPopup({ type: ACCOUNTING_WIDGET_TYPE, index: -1 });

  const recipientContent = (
    <>
      {imageSrc && (
        <Image
          src={imageSrc}
          alt={item.title}
          width={28}
          height={28}
          className={styles.recipientLogo}
        />
      )}
      <span className={styles.recipientName}>{item.title}</span>
      {canSwitch && (
        <span className={styles.recipientCaret} aria-hidden="true">
          <ChevronDown size={14} />
        </span>
      )}
    </>
  );

  return (
    <>
      <div className="box head">
        <h4 className={styles.header}>
          {getButtonText(ACCOUNTING_WIDGET_TYPE, lang)}
        </h4>
        {closeButton}
      </div>

      <div className="box">
        <p className={styles.label}>
          {translate(lang, "Mottaker", "Recipient")}
        </p>

        {canSwitch ? (
          <button
            type="button"
            className={styles.recipient}
            onClick={openSwitcher}
            aria-label={translate(
              lang,
              "Bytt regnskapssystem",
              "Change accounting system"
            )}
          >
            {recipientContent}
          </button>
        ) : (
          <div className={`${styles.recipient} ${styles.recipientStatic}`}>
            {recipientContent}
          </div>
        )}

        <EmailExportSection
          userId={userId}
          receiptId={receiptId}
          lang={lang}
          settingsId={item.receipt_view_service_settings_id}
          storeName={storeName}
          initialEmail={getStoredEmail(item.receipt_view_service_settings_id)}
          onSendSuccess={onAccountingSendSuccess(item)}
        />
      </div>
    </>
  );
};
